import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import useInAppBrowser from '../../../hooks/useInAppBrowser';
import { PLATFORM_ICON_MAP } from '../../Icons/V2';
import { getMaskedUrl } from '../../../lib/url';
import {
  getEncodedExternalBrowserRedirectUrl,
  getExternalBrowserRedirectSearchValue,
} from '../../../lib/external-browser';
import useIsInstagramBrowser from '../../../hooks/useIsInstagramBrowser';
import { isDisclaimerPlatform } from '../../../lib/platform';

export const useLinkCard = ({
  platform,
  url,
  icon,
  creatorName,
  withDisclaimer,
  withSensitiveLinkHoldToOpen = false,
  customIconClassName,
  disableDeeplinks = false,
}) => {
  const isInstagram = useIsInstagramBrowser();

  const [isDisclaimerVisible, setIsDisclaimerVisible] = useState(false);
  const [didNavigationSilentBlock, setDidNavigationSilentBlock] =
    useState(false);

  const isSensitiveLink = withDisclaimer || isDisclaimerPlatform(platform);

  const shouldUseInstagramHoldToOpen =
    withSensitiveLinkHoldToOpen && isInstagram && isSensitiveLink;

  const shouldForceExternalBrowser =
    isInstagram &&
    !disableDeeplinks &&
    isSensitiveLink &&
    !shouldUseInstagramHoldToOpen;

  const maskedUrl = useMemo(
    () =>
      getMaskedUrl(url, {
        withSensitiveLinkHoldToOpen: shouldUseInstagramHoldToOpen,
        disableDeeplinks,
        forceMask: shouldUseInstagramHoldToOpen,
      }),
    [url, shouldUseInstagramHoldToOpen, disableDeeplinks]
  );

  const platformIcon = useMemo(() => {
    if (platform && PLATFORM_ICON_MAP[platform]) {
      return PLATFORM_ICON_MAP[platform]();
    }
    return (
      <GatsbyImage
        image={icon}
        alt={`${platform || 'Custom'} icon`}
        className={customIconClassName}
      />
    );
  }, [platform, icon, customIconClassName]);

  const handleSilentBlock = useCallback(() => {
    if (isInstagram) {
      setDidNavigationSilentBlock(true);
      setIsDisclaimerVisible(true);
    }
  }, [isInstagram]);

  const { handleLinkClick } = useInAppBrowser({
    creatorName,
    preferInAppBrowser: !shouldForceExternalBrowser,
    onSilentBlock: handleSilentBlock,
  });

  const onLinkClick = useCallback(
    (e) => {
      e.preventDefault();
      handleLinkClick(maskedUrl);
    },
    [handleLinkClick, maskedUrl]
  );

  const openDisclaimer = useCallback(() => {
    setDidNavigationSilentBlock(false);
    setIsDisclaimerVisible(true);
  }, []);

  const closeDisclaimer = useCallback(() => {
    setDidNavigationSilentBlock(false);
    setIsDisclaimerVisible(false);
  }, []);

  useEffect(() => {
    setDidNavigationSilentBlock(false);
  }, [url]);

  useEffect(() => {
    if (typeof window === 'undefined' || !isInstagram || disableDeeplinks) {
      return;
    }

    const currentUrlIntent = getExternalBrowserRedirectSearchValue(
      window.location.search
    );
    if (!currentUrlIntent) {
      return;
    }

    const encodedRedirectUrl = getEncodedExternalBrowserRedirectUrl(url);
    if (currentUrlIntent === encodedRedirectUrl) {
      setIsDisclaimerVisible(true);
    }
  }, [isInstagram, disableDeeplinks, url]);

  const Link = useCallback(
    ({ children, className }) => (
      <a
        className={className}
        href={maskedUrl}
        target="_blank"
        rel="noreferrer"
        onClick={onLinkClick}
      >
        {children}
      </a>
    ),
    [onLinkClick, maskedUrl]
  );

  const CardWrapper = useCallback(
    ({ children, linkClassName, buttonClassName }) => {
      if (isSensitiveLink) {
        return (
          <button
            type="button"
            className={buttonClassName}
            onClick={openDisclaimer}
          >
            {children}
          </button>
        );
      }
      return <Link className={linkClassName}>{children}</Link>;
    },
    [isSensitiveLink, openDisclaimer]
  );

  return {
    platformIcon,
    isDisclaimerVisible,
    isInstagram,
    didNavigationSilentBlock,
    openDisclaimer,
    closeDisclaimer,
    shouldShowDisclaimer: isSensitiveLink,
    Link,
    CardWrapper,
  };
};
